import { AppFilterType, filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { HiringPlanTable } from "@/pages/recruit/hiring-plan/components/HiringPlanTable.tsx";
import { RequestPositionEditModal } from "@/pages/recruit/hiring-plan/widgets/RequestPositionEditModal.tsx";
import { useHiringPlanSearch } from "@/queries/recruit/use-hiring-plan.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import {
  ApiHiringPlanFilter,
  getHiringPlanRequestStatuses,
} from "@/types/recruit/hiring-plan.types.ts";
import {
  Button,
  CoreFilterType,
  FilterRenderType,
  FilterValueType,
  FontAwesomeIcon,
  Page,
  PaginationBlock,
  Search,
  Stack,
  useDebouncedSearchParam,
  useModals,
} from "@skailer-com/uikit";
import { NotificationCount } from "@/components/notification-count/NotificationCount.tsx";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useEffectOnce } from "react-use";

export function HiringPlanPage() {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const [, setSearchParams] = useSearchParams();
  const {
    value: searchTerm,
    setValue: setSearchTerm,
    clear: clearSearch,
  } = useDebouncedSearchParam({
    paramName: "search",
    delay: 300,
  });

  const library = filtersLibrary();

  const {
    filter: { filter },
    filterButton,
  } = useAppFilter<ApiHiringPlanFilter>({
    filters: [
      library.page_number,
      library.text,
      library.division_ids,
      library.department_ids,
      library.location_ids,
      {
        name: "statuses",
        type: CoreFilterType.hidden,
        valueType: FilterValueType.stringArray,
        renderType: FilterRenderType.multiselect,
        label: t("common:status"),
        options: getHiringPlanRequestStatuses(),
      },
      {
        name: "date_plan_from",
        type: CoreFilterType.hidden,
        valueType: FilterValueType.date,
        renderType: FilterRenderType.date,
        label: t("common:start_date"),
      },
      {
        name: "date_plan_to",
        type: CoreFilterType.hidden,
        valueType: FilterValueType.date,
        renderType: FilterRenderType.date,
        label: t("hiring_plan:plan_date"),
      },
      {
        name: "created_by_ids",
        type: AppFilterType.employee,
        valueType: FilterValueType.numberArray,
        renderType: FilterRenderType.custom,
        label: t("common:created_by"),
      },
    ],
    source: "hiring_plan",
    renderFilterButton: ({ onClick, count }) => (
      <Button
        variant="secondary"
        leftIcon={<FontAwesomeIcon icon="fa-light fa-bars-filter" />}
        rightIcon={count > 0 ? <NotificationCount count={count} size="sm" /> : undefined}
        onClick={onClick}
      >
        {t("common:filter")}
      </Button>
    ),
  });

  const { data: hiringPlan, isLoading, isFetching } = useHiringPlanSearch(filter);

  useTitle(t("hiring_plan:hiring_plan"), t("ats:recruit"));

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.hiringPlan.viewed);
  });

  return (
    <Page className="h-full">
      <Page.Header title={t("hiring_plan:hiring_plan")}>
        <Stack direction={"horizontal"} gap={"sm"}>
          {filterButton()}
          <Button
            leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
            onClick={() => {
              openModal(RequestPositionEditModal);
            }}
          >
            {t("hiring_plan:request_new_position")}
          </Button>
        </Stack>
      </Page.Header>
      <Page.Content stretch>
        <Page.StickyLayout
          toolbar={
            <Search
              onInput={(e) => setSearchTerm(e.currentTarget.value)}
              type={"text"}
              value={searchTerm}
              placeholder={t("common:search")}
              clearable={true}
              onClear={clearSearch}
            />
          }
          footer={
            <PaginationBlock
              metadata={hiringPlan?.metadata}
              onPageChange={(page) => {
                setSearchParams((searchParams) => {
                  searchParams.set("page", page.toString());
                  return searchParams;
                });
              }}
            />
          }
        >
          <HiringPlanTable
            items={hiringPlan?.items ?? []}
            isLoading={isLoading}
            isFetching={isFetching}
            onPageChange={(page) => {
              setSearchParams((searchParams) => {
                searchParams.set("page", page.toString());
                return searchParams;
              });
            }}
            isFiltering={!!filter.text}
            metadata={hiringPlan?.metadata}
            stickyLayout
          />
        </Page.StickyLayout>
      </Page.Content>
    </Page>
  );
}
